import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { CONTACT_CATS, SPONSOR_OPPS } from "@/lib/content";

const empty = { name: "", email: "", phone: "", category: "", organization: "", message: "", interests: [] };

export const InquiryForm = ({ type = "contact", prefix = "contact" }) => {
  const sponsor = type === "sponsorship";
  const defCat = sponsor ? "Sponsorship" : "General Information";
  const [f, setF] = useState({ ...empty, category: defCat });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggle = (k) => setF({ ...f, interests: f.interests.includes(k) ? f.interests.filter((x) => x !== k) : [...f.interests, k] });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.category) return toast.error("Please choose an inquiry category.");
    setBusy(true);
    try {
      await api.post("/inquiries", { ...f, type });
      setDone(true);
      setF({ ...empty, category: defCat });
      toast.success("Thank you! Your message was sent to Tri County Allstars.");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="card-navy p-8" data-testid={`${prefix}-form-success`}>
        <CheckCircle2 className="h-10 w-10 text-gold" />
        <p className="mt-4 font-display text-3xl font-black uppercase italic">Message received.</p>
        <p className="mt-2 text-sm text-slate-300">Coach KP and the Tri County Allstars staff will follow up with you soon.</p>
        <button onClick={() => setDone(false)} className="mt-6 text-sm font-semibold uppercase tracking-wider text-gold hover:text-white" data-testid={`${prefix}-form-send-another`}>Send another message</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="card-navy p-6 sm:p-8 space-y-5" data-testid={`${prefix}-form`}>
      <div className="grid gap-5 sm:grid-cols-2">
        <div><label className="label" htmlFor={`${prefix}-name`}>Name *</label><input id={`${prefix}-name`} required className="field" value={f.name} onChange={set("name")} data-testid={`${prefix}-form-name`} /></div>
        <div><label className="label" htmlFor={`${prefix}-email`}>Email Address *</label><input id={`${prefix}-email`} type="email" required className="field" value={f.email} onChange={set("email")} data-testid={`${prefix}-form-email`} /></div>
        <div><label className="label" htmlFor={`${prefix}-phone`}>Phone Number</label><input id={`${prefix}-phone`} type="tel" className="field" value={f.phone} onChange={set("phone")} data-testid={`${prefix}-form-phone`} /></div>
        {sponsor ? (
          <div><label className="label" htmlFor={`${prefix}-org`}>Business / Organization</label><input id={`${prefix}-org`} className="field" value={f.organization} onChange={set("organization")} data-testid={`${prefix}-form-organization`} /></div>
        ) : (
          <div>
            <label className="label" htmlFor={`${prefix}-category`}>Inquiry Category *</label>
            <select id={`${prefix}-category`} className="field" value={f.category} onChange={set("category")} data-testid={`${prefix}-form-category`}>
              {CONTACT_CATS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        )}
      </div>
      {sponsor && (
        <div>
          <p className="label">Interested In</p>
          <div className="flex flex-wrap gap-2">
            {SPONSOR_OPPS.map(({ key }) => (
              <button type="button" key={key} onClick={() => toggle(key)} data-testid={`${prefix}-interest-${key.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-3 py-2 text-xs font-semibold uppercase tracking-wider border transition-colors ${f.interests.includes(key) ? "bg-gold text-navy border-gold" : "border-line text-slate-300 hover:border-gold"}`}>
                {key}
              </button>
            ))}
          </div>
        </div>
      )}
      <div><label className="label" htmlFor={`${prefix}-message`}>Message *</label><textarea id={`${prefix}-message`} required rows={5} className="field resize-y" value={f.message} onChange={set("message")} data-testid={`${prefix}-form-message`} /></div>
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
        <p className="text-xs text-slate-500 max-w-sm">Submissions go directly to Tri County Allstars at kelvinpollock370@icloud.com. We never share your information.</p>
        <button type="submit" disabled={busy} className="btn-gold" data-testid={`${prefix}-form-submit`}>
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} {sponsor ? "Send Sponsorship Inquiry" : "Send Message"}
        </button>
      </div>
    </form>
  );
};
